import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import DirectionsWalkIcon from "../icons/DirectionsWalkIcon";
import colours from "../../theme/colours";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_ENABLED,
  GOOGLE_MAPS_MAP_ID,
} from "../../config/googleMaps";
import { loadGoogleMapsApi } from "../../utils/googleMapsApi";
import {
  buildGoogleStaticMapUrl,
  normaliseGeoRoutePoint,
} from "../../utils/results/sixMinuteWalkRouteMap";
import SixMinuteWalkRouteChart from "./SixMinuteWalkRouteChart";

function hexToRgba(hex, alpha) {
  if (typeof hex !== "string") {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const normalized = hex.replace("#", "").trim();
  if (normalized.length !== 6) {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);

  if (![r, g, b].every(Number.isFinite)) {
    return `rgba(0, 0, 0, ${alpha})`;
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const MAP_BACKGROUND_COLOR = colours.card;
const ROUTE_STROKE_COLOR = colours.primary;
const START_MARKER_STROKE = colours.secondary;
const END_MARKER_FILL = colours.primary;
const EMPTY_MAP_BACKGROUND_COLOR = colours.card;
const EMPTY_MAP_GRID_COLOR = hexToRgba(colours.secondary, 0.1);
const EMPTY_MAP_PRIMARY_GLOW = hexToRgba(colours.primary, 0.12);
const EMPTY_MAP_TERTIARY_GLOW = hexToRgba(colours.tertiary, 0.18);
const CURRENT_LOCATION_SHADOW = hexToRgba(colours.primary, 0.24);
const MAP_DEBUG_ENABLED = process.env.NODE_ENV === "development";

function buildMapSurfaceClassName({ className = "", fillContainer = false }) {
  return [
    "flex w-full items-center justify-center overflow-hidden rounded-2xl bg-greyscale-7",
    fillContainer ? "h-full min-h-0" : "aspect-[4/3]",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

function logMapDebug(level, message, details = undefined) {
  if (!MAP_DEBUG_ENABLED || typeof console === "undefined") {
    return;
  }

  const logger =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : console.info;

  if (details === undefined) {
    logger(`[SixMinuteWalkRouteMap] ${message}`);
    return;
  }

  logger(`[SixMinuteWalkRouteMap] ${message}`, details);
}

function buildMarkerIcon(googleMaps, { fillColor, strokeColor }) {
  return new googleMaps.marker.PinElement({
    background: fillColor,
    borderColor: strokeColor,
    glyphColor: fillColor,
    scale: 0.9,
  });
}

function buildCurrentLocationMarkerContent() {
  if (typeof document === "undefined") {
    return null;
  }

  const markerElement = document.createElement("div");
  markerElement.className =
    "flex h-11 w-11 items-center justify-center rounded-full border-2 border-primary bg-white text-primary";
  markerElement.style.boxShadow = `0 10px 24px ${CURRENT_LOCATION_SHADOW}`;
  markerElement.innerHTML = renderToStaticMarkup(
    <DirectionsWalkIcon aria-hidden="true" className="!h-6 !w-6" />,
  );
  return markerElement;
}

function applyMarkerContent(marker, content) {
  if (!marker || !content || typeof marker.replaceChildren !== "function") {
    return;
  }

  marker.replaceChildren(content);
}

function EmptyRouteMapShell({ ariaLabel, className, height, fillContainer }) {
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={buildMapSurfaceClassName({ className, fillContainer })}
      style={{ height }}
    >
      <div
        aria-hidden="true"
        className="relative h-full w-full overflow-hidden"
        style={{
          backgroundColor: EMPTY_MAP_BACKGROUND_COLOR,
          backgroundImage: [
            `linear-gradient(90deg, ${EMPTY_MAP_GRID_COLOR} 1px, transparent 1px)`,
            `linear-gradient(${EMPTY_MAP_GRID_COLOR} 1px, transparent 1px)`,
            `radial-gradient(circle at 18% 24%, ${EMPTY_MAP_PRIMARY_GLOW}, transparent 22%)`,
            `radial-gradient(circle at 78% 72%, ${EMPTY_MAP_TERTIARY_GLOW}, transparent 28%)`,
          ].join(", "),
          backgroundPosition: "0 0, 0 0, 0 0, 0 0",
          backgroundSize: "32px 32px, 32px 32px, auto, auto",
        }}
      >
        <svg
          viewBox="0 0 240 160"
          className="absolute inset-0 h-full w-full text-primary/35"
          preserveAspectRatio="none"
        >
          <path
            d="M28 118C54 94 76 86 102 92C128 98 146 72 172 70C190 68 206 80 224 96"
            fill="none"
            stroke="currentColor"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.22"
          />
          <path
            d="M28 118C54 94 76 86 102 92C128 98 146 72 172 70C190 68 206 80 224 96"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx="28"
            cy="118"
            r="6"
            fill="#ffffff"
            stroke={START_MARKER_STROKE}
            strokeWidth="3"
          />
          <circle
            cx="224"
            cy="96"
            r="7"
            fill={END_MARKER_FILL}
            stroke="#ffffff"
            strokeWidth="3"
          />
        </svg>

        <div className="absolute inset-x-0 top-4 flex justify-center px-4">
          <div className="rounded-full bg-white/88 p-3 shadow-sm backdrop-blur-sm">
            <DirectionsWalkIcon aria-hidden="true" className="!h-5 !w-5 text-primary" />
          </div>
        </div>
      </div>
    </div>
  );
}

const SixMinuteWalkRouteMap = memo(function SixMinuteWalkRouteMap({
  geoPoints = [],
  fallbackPoints = [],
  ariaLabel = "Walk route trace",
  height = 240,
  className = "",
  fillContainer = false,
  showEmptyState = false,
  showCurrentLocationIcon = false,
}) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsLoadFailed, setMapsLoadFailed] = useState(false);
  const lastLoggedStateRef = useRef("");
  const normalisedGeoPoints = useMemo(
    () =>
      (Array.isArray(geoPoints) ? geoPoints : [])
        .map((point, index) => normaliseGeoRoutePoint(point, index))
        .filter(Boolean),
    [geoPoints],
  );
  const staticMapUrl = useMemo(
    () =>
      buildGoogleStaticMapUrl(normalisedGeoPoints, {
        apiKey: GOOGLE_MAPS_API_KEY,
      }),
    [normalisedGeoPoints],
  );
  const renderMode = useMemo(() => {
    if (!normalisedGeoPoints.length) {
      if (fallbackPoints.length) {
        return "fallback-chart-no-geo-points";
      }

      return showEmptyState ? "empty-shell" : "empty";
    }

    if (!GOOGLE_MAPS_ENABLED) {
      return fallbackPoints.length
        ? "fallback-chart-google-maps-disabled"
        : "empty";
    }

    if ((!mapsReady || mapsLoadFailed) && !staticMapUrl && fallbackPoints.length) {
      return "fallback-chart-static-map-unavailable";
    }

    if (!mapsReady || mapsLoadFailed) {
      return "static-map";
    }

    return "interactive-map";
  }, [
    fallbackPoints.length,
    mapsLoadFailed,
    mapsReady,
    normalisedGeoPoints.length,
    showEmptyState,
    staticMapUrl,
  ]);

  useEffect(() => {
    const stateKey = [
      renderMode,
      normalisedGeoPoints.length > 0 ? "geo" : "no-geo",
      fallbackPoints.length > 0 ? "fallback" : "no-fallback",
      GOOGLE_MAPS_ENABLED ? "maps-enabled" : "maps-disabled",
      mapsReady ? "ready" : "not-ready",
      mapsLoadFailed ? "load-failed" : "load-ok",
      staticMapUrl ? "static-url" : "no-static-url",
    ].join("|");

    if (!MAP_DEBUG_ENABLED || lastLoggedStateRef.current === stateKey) {
      return;
    }

    lastLoggedStateRef.current = stateKey;
    logMapDebug("info", "Route map render state changed.", {
      renderMode,
      geoPointCount: normalisedGeoPoints.length,
      fallbackPointCount: fallbackPoints.length,
      googleMapsEnabled: GOOGLE_MAPS_ENABLED,
      apiKeyPresent: Boolean(GOOGLE_MAPS_API_KEY),
      mapsReady,
      mapsLoadFailed,
      staticMapUrlPresent: Boolean(staticMapUrl),
      firstGeoPoint: normalisedGeoPoints[0] || null,
      lastGeoPoint:
        normalisedGeoPoints[normalisedGeoPoints.length - 1] || null,
    });
  }, [
    fallbackPoints.length,
    mapsLoadFailed,
    mapsReady,
    normalisedGeoPoints,
    renderMode,
    staticMapUrl,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!GOOGLE_MAPS_ENABLED) {
      logMapDebug(
        "warn",
        "Skipping Google Maps JavaScript API load because the browser key is missing or blank. If you just added REACT_APP_GOOGLE_MAPS_API_KEY in .env.local, restart npm start so the bundle picks it up.",
        {
          apiKeyPresent: Boolean(GOOGLE_MAPS_API_KEY),
          envVarName: "REACT_APP_GOOGLE_MAPS_API_KEY",
        },
      );
      setMapsReady(false);
      setMapsLoadFailed(false);
      return undefined;
    }

    if (!normalisedGeoPoints.length) {
      logMapDebug(
        "info",
        "Skipping Google Maps JavaScript API load because there are no geo route points yet.",
      );
      setMapsReady(false);
      setMapsLoadFailed(false);
      return undefined;
    }

    setMapsLoadFailed(false);
    logMapDebug("info", "Loading Google Maps JavaScript API.", {
      geoPointCount: normalisedGeoPoints.length,
      staticMapUrlPresent: Boolean(staticMapUrl),
    });

    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY)
      .then((googleMaps) => {
        if (!cancelled && googleMaps) {
          logMapDebug("info", "Google Maps JavaScript API loaded.", {
            geoPointCount: normalisedGeoPoints.length,
          });
          setMapsReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          logMapDebug("error", "Google Maps JavaScript API failed to load.", {
            message: error?.message || String(error),
            geoPointCount: normalisedGeoPoints.length,
            staticMapUrlPresent: Boolean(staticMapUrl),
          });
          setMapsReady(false);
          setMapsLoadFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [normalisedGeoPoints.length, staticMapUrl]);

  useEffect(() => {
    if (
      !mapsReady ||
      !mapElementRef.current ||
      !normalisedGeoPoints.length ||
      !window.google?.maps
    ) {
      return undefined;
    }

    const googleMaps = window.google.maps;
    const AdvancedMarkerElement =
      googleMaps.marker?.AdvancedMarkerElement || null;
    const points = normalisedGeoPoints.map((point) => ({
      lat: point.lat,
      lng: point.lng,
    }));

    if (!AdvancedMarkerElement || !googleMaps.marker?.PinElement) {
      logMapDebug(
        "error",
        "Advanced marker library is unavailable; skipping interactive markers.",
      );
      return undefined;
    }

    if (!mapRef.current) {
      mapRef.current = new googleMaps.Map(mapElementRef.current, {
        backgroundColor: MAP_BACKGROUND_COLOR,
        clickableIcons: false,
        disableDefaultUI: true,
        fullscreenControl: false,
        gestureHandling: "none",
        keyboardShortcuts: false,
        mapId: GOOGLE_MAPS_MAP_ID,
        mapTypeControl: false,
        streetViewControl: false,
      });
      logMapDebug("info", "Created interactive Google map instance.", {
        geoPointCount: points.length,
        mapId: GOOGLE_MAPS_MAP_ID,
      });
    }

    const map = mapRef.current;

    if (!polylineRef.current) {
      polylineRef.current = new googleMaps.Polyline({
        geodesic: true,
        map,
        strokeColor: ROUTE_STROKE_COLOR,
        strokeOpacity: 1,
        strokeWeight: 4,
      });
    }

    polylineRef.current.setPath(points);

    const showCurrentLocationOnSinglePoint =
      showCurrentLocationIcon && points.length === 1;

    if (!startMarkerRef.current) {
      startMarkerRef.current = new AdvancedMarkerElement({
        gmpClickable: false,
        map,
        position: points[0],
        title: showCurrentLocationOnSinglePoint
          ? "Current location"
          : "Walk start",
      });
    }

    startMarkerRef.current.map = map;
    startMarkerRef.current.position = points[0];
    startMarkerRef.current.title = showCurrentLocationOnSinglePoint
      ? "Current location"
      : "Walk start";
    applyMarkerContent(
      startMarkerRef.current,
      showCurrentLocationOnSinglePoint
        ? buildCurrentLocationMarkerContent()
        : buildMarkerIcon(googleMaps, {
            fillColor: "#ffffff",
            strokeColor: START_MARKER_STROKE,
          }),
    );

    if (points.length > 1) {
      if (!endMarkerRef.current) {
        endMarkerRef.current = new AdvancedMarkerElement({
          gmpClickable: false,
          map,
          position: points[points.length - 1],
          title: showCurrentLocationIcon ? "Current location" : "Walk end",
        });
      }

      endMarkerRef.current.map = map;
      endMarkerRef.current.position = points[points.length - 1];
      endMarkerRef.current.title = showCurrentLocationIcon
        ? "Current location"
        : "Walk end";
      applyMarkerContent(
        endMarkerRef.current,
        showCurrentLocationIcon
          ? buildCurrentLocationMarkerContent()
          : buildMarkerIcon(googleMaps, {
              fillColor: END_MARKER_FILL,
              strokeColor: "#ffffff",
            }),
      );
    } else if (endMarkerRef.current) {
      endMarkerRef.current.map = null;
    }

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(17);
      logMapDebug("info", "Rendered Google map with a single route point.", {
        point: points[0],
      });
      return undefined;
    }

    const bounds = new googleMaps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 24);
    logMapDebug("info", "Updated Google map route bounds.", {
      geoPointCount: points.length,
      startPoint: points[0],
      endPoint: points[points.length - 1],
    });

    return undefined;
  }, [mapsReady, normalisedGeoPoints, showCurrentLocationIcon]);

  useEffect(
    () => () => {
      polylineRef.current?.setMap?.(null);
      if (startMarkerRef.current) {
        startMarkerRef.current.map = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.map = null;
      }
    },
    [],
  );

  if (!normalisedGeoPoints.length) {
    if (fallbackPoints.length) {
      return (
        <SixMinuteWalkRouteChart
          points={fallbackPoints}
          ariaLabel={ariaLabel}
          height={height}
          className={className}
          fillContainer={fillContainer}
        />
      );
    }

    return showEmptyState ? (
      <EmptyRouteMapShell
        ariaLabel={ariaLabel}
        className={className}
        height={height}
        fillContainer={fillContainer}
      />
    ) : null;
  }

  if (!GOOGLE_MAPS_ENABLED && fallbackPoints.length) {
    return (
      <SixMinuteWalkRouteChart
        points={fallbackPoints}
        ariaLabel={ariaLabel}
        height={height}
        className={className}
        fillContainer={fillContainer}
      />
    );
  }

  if ((!mapsReady || mapsLoadFailed) && !staticMapUrl && fallbackPoints.length) {
    return (
      <SixMinuteWalkRouteChart
        points={fallbackPoints}
        ariaLabel={ariaLabel}
        height={height}
        className={className}
        fillContainer={fillContainer}
      />
    );
  }

  if (!mapsReady || mapsLoadFailed) {
    return staticMapUrl ? (
      <div
        role="img"
        aria-label={ariaLabel}
        className={buildMapSurfaceClassName({ className, fillContainer })}
        style={{ height }}
      >
        <img
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover"
          onError={(event) => {
            logMapDebug("error", "Static Google map image failed to load.", {
              src: event.currentTarget.currentSrc || staticMapUrl,
              geoPointCount: normalisedGeoPoints.length,
            });
          }}
          onLoad={(event) => {
            logMapDebug("info", "Static Google map image loaded.", {
              src: event.currentTarget.currentSrc || staticMapUrl,
              geoPointCount: normalisedGeoPoints.length,
            });
          }}
          src={staticMapUrl}
        />
      </div>
    ) : null;
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className={buildMapSurfaceClassName({ className, fillContainer })}
      style={{ height }}
    >
      <div ref={mapElementRef} aria-hidden="true" className="h-full w-full" />
    </div>
  );
});

export default SixMinuteWalkRouteMap;
